import { toast } from "sonner";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { formatApiError, http, setEditionState, edition } from "@/lib/api";

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [tutorContext, setTutorContext] = useState({});
  const [buddy, setBuddy] = useState(undefined);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [guestState, setGuestState] = useState(edition.state);
  const chooseGuestState = useCallback((st) => { setEditionState(st); setGuestState(st); }, []);

  useEffect(() => {
    if (profile?.role !== "student") { setBuddy(undefined); return; }
    http.get("/buddy").then((r) => setBuddy(r.data.buddy)).catch(() => setBuddy(null));
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    http.get("/auth/me").then((r) => { setEditionState(r.data.state || "ga"); setProfile(r.data); }).catch(() => setProfile(false));
  }, []);

  const setGrade = useCallback(async (grade) => {
    let prev;
    setProfile((p) => { prev = p; return { ...p, grade }; });
    try { setProfile((await http.put("/auth/me", { grade })).data); }
    catch (e) { setProfile(prev); toast.error(formatApiError(e)); }
  }, []);

  const setReadSpeed = useCallback(async (read_speed) => {
    setProfile((p) => ({ ...p, read_speed }));
    try { setProfile((await http.put("/auth/me", { read_speed })).data); } catch { /* keep local choice */ }
  }, []);

  const setSpanish = useCallback(async (spanish_help) => {
    setProfile((p) => ({ ...p, spanish_help }));
    try { setProfile((await http.put("/auth/me", { spanish_help })).data); } catch { setProfile((p) => ({ ...p, spanish_help: !spanish_help })); }
  }, []);

  const setStateEdition = useCallback(async (state) => {
    const r = await http.put("/auth/me", { state });
    setEditionState(state);
    setProfile(r.data);
  }, []);

  const login = useCallback(async (email, password, kid) => {
    const r = kid ? await http.post("/auth/kid-login", kid) : await http.post("/auth/login", { email, password });
    setEditionState(r.data.state || "ga");
    setProfile(r.data);
    return r.data;
  }, []);

  const register = useCallback(async (body) => {
    const r = await http.post("/auth/register", body);
    setEditionState(r.data.state || "ga");
    setProfile(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    await http.post("/auth/logout").catch(() => {});
    setTutorOpen(false);
    chooseGuestState("ga");
    setProfile(false);
  }, [chooseGuestState]);

  return (
    <ProfileContext.Provider value={{ profile, setGrade, setReadSpeed, setSpanish, setStateEdition, login, register, logout, tutorOpen, setTutorOpen, tutorContext, setTutorContext, buddy, setBuddy, checkinOpen, setCheckinOpen, todayCheckin, setTodayCheckin, guestState, chooseGuestState }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
